import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BasePagination from 'components/Shared/Pagination';
import { colors } from 'components/styles/variables';
import { DrivingExperiences, fetchDrivingExperiencesOfCity } from 'lib/AutolistAPI';

import PollCard from './PollCard';
import DrivingExperience from './DrivingExperience';

export interface DrivingTipsAndExperienceProps {
  drivingExperiences: DrivingExperiences;
}

export default function DrivingTipsAndExperience({
  drivingExperiences: initialExperiences,
}: DrivingTipsAndExperienceProps): JSX.Element {
  const { experiences, carNeed, pagination, cityId } = initialExperiences;
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(pagination?.currentPage);
  const [drivingExperiences, setDrivingExperiences] = useState(initialExperiences);
  const layoutScrollRef = useRef<HTMLDivElement>(null);

  const cityState = experiences?.[0].city;
  const city = cityState.split(', ')[0];

  async function fetchDrivingExperiences(page: number): Promise<void> {
    const drivingExperiencesRes = await fetchDrivingExperiencesOfCity(cityId, page);
    if (drivingExperiencesRes) {
      setDrivingExperiences(drivingExperiencesRes);
    }

    if (layoutScrollRef?.current && page >= 1) {
      layoutScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function onPageChange(page: number): void {
    setCurrentPage(page);
    fetchDrivingExperiences(page);
  }

  return (
    <>
      <div ref={layoutScrollRef}>
        <h2 className="section-header">
          {cityState} {t('seoSrp.drivingTipsAndExperiences.header')}
        </h2>
        <div className="experience-container">
          <div className="left">
            <PollCard title={city} breakdownByCarNeed={carNeed} />
          </div>
          <div className="right">
            <>
              {drivingExperiences.experiences.map((experience) => (
                <DrivingExperience key={experience.id} review={experience} />
              ))}
              <div className="pagination">
                <BasePagination
                  currentPage={currentPage}
                  onPageChange={onPageChange}
                  pageSize={2}
                  totalResults={pagination?.totalPages * 2}
                />
              </div>
            </>
          </div>
        </div>
      </div>
      <style jsx>{`
        .section-header {
          font-weight: 700;
          font-size: 18px;
          line-height: 24px;
          letter-spacing: 0.2px;
          text-transform: uppercase;
          color: ${colors.BLACK};
          margin: 24px 10px 24px 10px;
        }
        .pagination {
          margin: 0 12px;
          padding-bottom: 44px;
          display: flex;
          justify-content: center;
        }
        .experience-container {
          display: flex;
          flex-direction: column;
        }
        @media (min-width: 850px) {
          .pagination {
            margin: 0;
          }
          .experience-container {
            display: flex;
            flex-direction: row;
          }
          .right {
            padding-left: 32px;
          }
          .section-header {
            font-weight: 700;
            font-size: 20px;
            line-height: 26px;
            letter-spacing: 0.2px;
            text-transform: uppercase;
            color: ${colors.BLACK};
            margin: 24px 0 24px 24px;
          }
        }
        @media (min-width: 1440px) {
          .section-header {
            margin-left: 0;
          }
        }
      `}</style>
    </>
  );
}
